// src/components/MyPendenzen.jsx
// Projektübergreifende Pendenzen-Ansicht (US-PEND-005).
// scope="mine": nur Pendenzen, für die die angemeldete Person verantwortlich ist.
// scope="all":  alle Projekte, gruppiert nach Projekt ("Projektübersicht").
// Bearbeiten erfolgt im Projekt (Zeilenklick → Sprung, US-PEND-002).
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  getAllAcrossProjects,
  SPECIAL_LISTS,
  updatePendenz,
} from "../services/azurePendenzenService";
import { getAllProjects } from "../services/azureProjectsService";
import { hasRole, ROLES } from "../utils/permissions";
import PendenzenBoard, { PendenzCard } from "./PendenzenBoard";
import PendenzEditModal from "./PendenzEditModal";
import { useAuth } from "../hooks/useAuth";
import { useFreelancerProjects } from "../hooks/useFreelancerProjects";
import { sanitizeRichText, richTextToPlainText } from "../utils/richText";
import "../styles/pendenzen.scss";

const ART_OPTIONS = [
  { value: "P", label: "Pendenz" },
  { value: "E", label: "Entscheid" },
  { value: "N", label: "Notiz" },
];
const KATEGORIE_OPTIONS = [
  { value: "Org", label: "Organisation" },
  { value: "Pl", label: "Planung" },
  { value: "Ko", label: "Kosten" },
  { value: "Te", label: "Termine" },
  { value: "Div", label: "Diverses" },
];

const STATUS_OPTIONS = [
  { value: "offen", label: "Offen" },
  { value: "inBearbeitung", label: "In Bearbeitung" },
  { value: "kritisch", label: "Kritisch" },
  { value: "erledigt", label: "Erledigt" },
];

const artLabel = (v) => ART_OPTIONS.find((o) => o.value === v)?.label || v;
const kategorieLabel = (v) =>
  KATEGORIE_OPTIONS.find((o) => o.value === v)?.label || v;
const statusLabel = (v) =>
  STATUS_OPTIONS.find((o) => o.value === v)?.label || v;

const todayIso = () => {
  const d = new Date();
  const tzOffset = d.getTimezoneOffset() * 60000;
  return new Date(d - tzOffset).toISOString().slice(0, 10);
};

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("de-CH", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const massgeblicherTermin = (p) => p.terminNeu || p.terminSoll || "";

const istUeberfaellig = (p) => {
  if (p.status === "erledigt") return false;
  const termin = massgeblicherTermin(p);
  if (!termin) return false;
  return termin < todayIso();
};

const MyPendenzen = ({ user: userProp, scope = "mine" }) => {
  const navigate = useNavigate();
  const auth = useAuth();
  const user = userProp || auth.user;
  const { canAccessProject, isFreelancerUser } = useFreelancerProjects(user);

  const [pendenzen, setPendenzen] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter – erledigte standardmässig ausgeblendet, wieder einblendbar
  const [filterArt, setFilterArt] = useState("");
  const [filterKategorie, setFilterKategorie] = useState("");
  const [showErledigt, setShowErledigt] = useState(false);
  const [search, setSearch] = useState("");

  // Ansicht (Liste/Board) für "Meine Pendenzen"
  const [view, setView] = useState("list");
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (!isFullscreen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setIsFullscreen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isFullscreen]);

  // Dialog "Neue Pendenzenliste" (scope="all")
  const [showNewList, setShowNewList] = useState(false);
  const [newListProjectCode, setNewListProjectCode] = useState("");

  // Dialog "Neue Pendenz" (scope="mine"): zuerst Projekt wählen, dann dort erfassen.
  const [showNewPendenz, setShowNewPendenz] = useState(false);
  const [newPendenzProjectCode, setNewPendenzProjectCode] = useState("");

  // Direktes Bearbeiten in der persönlichen Ansicht (ohne ins Projekt zu wechseln).
  const [editing, setEditing] = useState(null); // Pendenz oder null

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [pendData, projectsData] = await Promise.all([
        getAllAcrossProjects(),
        getAllProjects(),
      ]);
      setPendenzen(pendData);
      setProjects(projectsData);
    } catch (err) {
      setError(err.message || "Die Pendenzen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // projectUuid (= project.id) -> Projekt
  const projectMap = useMemo(() => {
    const map = {};
    projects.forEach((p) => {
      map[p.id] = p;
    });
    return map;
  }, [projects]);

  // Verantwortliche werden als team:<employeeUuid> gespeichert (US-PEND-002-Update,
  // siehe ADR 0011). "Meine" Pendenz = eigene employeeUuid ist verknüpft; als Fallback
  // für Alt-Freitext (US-PEND-001) der Namensabgleich.
  const myUuid = user?.teamMember?.employeeUuid || "";
  const userName = (user?.name || user?.teamMember?.name || "").toLowerCase();

  const isMine = useCallback(
    (p) => {
      if (myUuid && (p.verantwortlichIds || []).includes(`team:${myUuid}`)) {
        return true;
      }
      if (userName && p.verantwortlich) {
        return p.verantwortlich.toLowerCase().includes(userName);
      }
      return false;
    },
    [myUuid, userName]
  );

  // Sichtbare Grundmenge je nach Scope (inkl. Freelancer-Projektfilter)
  const scoped = useMemo(() => {
    return pendenzen.filter((p) => {
      const project = projectMap[p.projectUuid];
      if (isFreelancerUser) {
        if (!project || !canAccessProject(project.code)) return false;
      }
      if (scope === "mine") return isMine(p);
      return true;
    });
  }, [pendenzen, projectMap, isFreelancerUser, canAccessProject, isMine, scope]);

  // Filter + Suche + Sortierung (überfällige zuoberst, sonst Termin aufsteigend)
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const rows = scoped.filter((p) => {
      if (filterArt && p.typ !== filterArt) return false;
      if (filterKategorie && p.kategorie !== filterKategorie) return false;
      if (p.status === "erledigt" && !showErledigt) return false;
      if (term) {
        const project = projectMap[p.projectUuid];
        const haystack = `${richTextToPlainText(p.beschreibung)} ${p.verantwortlich} ${
          project?.code || ""
        } ${project?.title || ""}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });

    return rows.sort((a, b) => {
      const aOver = istUeberfaellig(a);
      const bOver = istUeberfaellig(b);
      if (aOver !== bOver) return aOver ? -1 : 1;
      const aT = massgeblicherTermin(a) || "9999-12-31";
      const bT = massgeblicherTermin(b) || "9999-12-31";
      return aT.localeCompare(bT);
    });
  }, [scoped, filterArt, filterKategorie, showErledigt, search, projectMap]);

  // Board (scope "mine"): Spalten = Projekte, Cards = meine Pendenzen je Projekt
  const { boardColumns, boardItems } = useMemo(() => {
    const byCol = {};
    const seen = [];
    filtered.forEach((p) => {
      const key = p.projectUuid || "__none";
      if (!byCol[key]) {
        byCol[key] = [];
        seen.push(key);
      }
      byCol[key].push(p);
    });
    const cols = seen
      .map((key) => {
        const proj = projectMap[key];
        return {
          key,
          label: proj ? `${proj.code} – ${proj.title}` : "Unbekannt",
          code: proj?.code || "",
          fixed: true,
        };
      })
      .sort((a, b) => (a.code || "").localeCompare(b.code || ""));
    return { boardColumns: cols, boardItems: byCol };
  }, [filtered, projectMap]);

  // Pendenzen-Zähler pro Projekt (offen/total) für die Projektübersicht
  const countsByProject = useMemo(() => {
    const m = {};
    pendenzen.forEach((p) => {
      const k = p.projectUuid;
      if (!m[k]) m[k] = { total: 0, open: 0 };
      m[k].total += 1;
      if (p.status !== "erledigt") m[k].open += 1;
    });
    return m;
  }, [pendenzen]);

  // Für die angemeldete Person zugängliche Projekte (Freelancer nur zugewiesene)
  const accessibleProjects = useMemo(
    () => projects.filter((p) => (isFreelancerUser ? canAccessProject(p.code) : true)),
    [projects, isFreelancerUser, canAccessProject]
  );

  // Gestartete Pendenzenlisten = zugängliche Projekte mit mindestens einem Eintrag
  // (wie bei den Terminen: die Übersicht startet leer und füllt sich mit neuen Listen).
  const startedLists = useMemo(() => {
    const term = search.trim().toLowerCase();
    return accessibleProjects
      .filter((p) => (countsByProject[p.id]?.total || 0) > 0)
      .filter((p) =>
        term ? `${p.code} ${p.title}`.toLowerCase().includes(term) : true
      )
      .sort((a, b) => (a.code || "").localeCompare(b.code || ""));
  }, [accessibleProjects, countsByProject, search]);

  // Projekte ohne Liste – Auswahl im "Neue Pendenzenliste"-Dialog
  const projectsWithoutList = useMemo(
    () =>
      accessibleProjects
        .filter((p) => (countsByProject[p.id]?.total || 0) === 0)
        .sort((a, b) => (a.code || "").localeCompare(b.code || "")),
    [accessibleProjects, countsByProject]
  );

  // Öffnet die Pendenzenliste innerhalb der Pendenzen-Shell (globale Nav + Subtabs
  // bleiben sichtbar, mit "Zurück zur Liste"-Button).
  const goToProject = (project) => {
    if (!project) {
      setError("Das zugehörige Projekt konnte nicht gefunden werden.");
      return;
    }
    navigate(`/main-pendenzen/projekte/${encodeURIComponent(project.code)}`);
  };

  // "Neue Pendenzenliste": Projekt wählen und dessen Pendenzenliste öffnen, wo der
  // erste Eintrag erfasst wird (die Tabelle wird dabei automatisch angelegt).
  const handleStartNewList = () => {
    const proj = projects.find((p) => p.code === newListProjectCode);
    if (!proj) {
      setError("Bitte ein Projekt auswählen.");
      return;
    }
    setShowNewList(false);
    navigate(`/main-pendenzen/projekte/${encodeURIComponent(proj.code)}`);
  };

  // Neue Pendenz aus "Meine Pendenzen": ins Projekt springen und dort direkt das
  // Erfassungsformular öffnen (via Navigations-State createNew).
  const goCreate = (project) => {
    if (!project) {
      setError("Das zugehörige Projekt konnte nicht gefunden werden.");
      return;
    }
    navigate(`/main-pendenzen/projekte/${encodeURIComponent(project.code)}`, {
      state: { createNew: true },
    });
  };
  const handleStartNewPendenz = () => {
    const proj = projects.find((p) => p.code === newPendenzProjectCode);
    if (!proj) {
      setError("Bitte ein Projekt auswählen.");
      return;
    }
    setShowNewPendenz(false);
    goCreate(proj);
  };

  // Status direkt in der persönlichen Ansicht umschalten (eigene Pendenz),
  // optimistisch, ohne ins Projekt zu wechseln.
  const handleChangeStatus = async (p, status) => {
    if (status === p.status) return;
    const erledigtAm = status === "erledigt" ? p.erledigtAm || todayIso() : "";
    const updated = { ...p, status, erledigtAm };
    setError(null);
    setPendenzen((prev) => prev.map((x) => (x.id === p.id ? updated : x)));
    try {
      await updatePendenz(p.projectUuid, p.id, updated);
    } catch (err) {
      setError(err.message || "Der Status konnte nicht geändert werden.");
      await loadData();
    }
  };

  const renderRow = (p, showProjectCol) => {
    const ueberfaellig = istUeberfaellig(p);
    const erledigt = p.status === "erledigt";
    const project = projectMap[p.projectUuid];
    return (
      <tr
        key={p.id}
        className={`pendenzen__row${erledigt ? " row-erledigt" : ""}`}
        onClick={() => setEditing(p)}
        title="Zum Bearbeiten anklicken"
      >
        {showProjectCol && (
          <td className="col-projekt">
            {project ? (
              <button
                type="button"
                className="pendenzen__projekt-jump"
                onClick={(e) => {
                  e.stopPropagation();
                  goToProject(project);
                }}
                title="Zum Projekt springen"
              >
                <span className="projekt-code">{project.code}</span>
                <span className="projekt-title">{project.title}</span>
              </button>
            ) : (
              <span className="projekt-unknown">Unbekannt</span>
            )}
          </td>
        )}
        <td className="col-nr">{p.nummer}</td>
        <td className="col-art">
          <span className={`art-badge art-badge--${p.typ}`} title={artLabel(p.typ)}>
            {p.typ}
          </span>
        </td>
        <td className="col-beschreibung">
          <span className="kategorie-chip">{kategorieLabel(p.kategorie)}</span>
          <span
            className="beschreibung-text rte-content"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: sanitizeRichText(p.beschreibung) }}
          />
        </td>
        <td className="col-verantwortlich">{p.verantwortlich}</td>
        <td
          className={`col-termin${
            ueberfaellig && !p.terminNeu ? " termin-ueberfaellig" : ""
          }`}
        >
          {formatDate(p.terminSoll)}
        </td>
        <td
          className={`col-termin${
            ueberfaellig && p.terminNeu ? " termin-ueberfaellig" : ""
          }`}
        >
          {formatDate(p.terminNeu)}
        </td>
        <td className="col-status">
          <select
            className={`status-select status-select--${p.status}`}
            value={p.status}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              e.stopPropagation();
              handleChangeStatus(p, e.target.value);
            }}
            title="Status ändern"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </td>
      </tr>
    );
  };

  const renderTable = (rows, showProjectCol) => {
    const colCount = showProjectCol ? 8 : 7;
    return (
      <div className="pendenzen__table-wrapper">
        <table className="pendenzen__table">
          <thead>
            <tr>
              {showProjectCol && <th className="col-projekt">Projekt</th>}
              <th className="col-nr">Nr</th>
              <th className="col-art">Art</th>
              <th className="col-beschreibung">Beschreibung</th>
              <th className="col-verantwortlich">Verantwortlich</th>
              <th className="col-termin">Deadline</th>
              <th className="col-termin">neue Deadline</th>
              <th className="col-status">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td className="pendenzen__empty" colSpan={colCount}>
                  Keine Pendenzen für die aktuelle Auswahl.
                </td>
              </tr>
            ) : (
              rows.map((p) => renderRow(p, showProjectCol))
            )}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="pendenzen pendenzen--loading">
        Pendenzen werden geladen...
      </div>
    );
  }

  const isAll = scope === "all";

  return (
    <div className={`pendenzen${isFullscreen ? " pendenzen--fullscreen" : ""}`}>
      <div className="pendenzen__header">
        <div>
          <h3>{isAll ? "Pendenzenlisten" : "Meine Pendenzen"}</h3>
          <p className="pendenzen__subtitle">
            {isAll
              ? "Bestehende Pendenzenlisten öffnen oder eine neue für ein Projekt starten."
              : "Alle offenen Punkte über alle Projekte, für die Sie verantwortlich sind. Zeile anklicken, um ins Projekt zu springen."}
          </p>
        </div>
        <div className="pendenzen__header-actions">
          {!isAll && (
            <div className="pendenzen__viewtoggle">
              <button
                type="button"
                className={`pendenzen__viewbtn${view === "list" ? " is-active" : ""}`}
                onClick={() => setView("list")}
              >
                Liste
              </button>
              <button
                type="button"
                className={`pendenzen__viewbtn${view === "board" ? " is-active" : ""}`}
                onClick={() => setView("board")}
              >
                Board
              </button>
            </div>
          )}
          <button
            type="button"
            className="pendenzen__btn"
            onClick={() => setIsFullscreen((v) => !v)}
          >
            {isFullscreen ? "Vollbild beenden" : "Vollbild"}
          </button>
          {!isAll && (
            <button
              type="button"
              className="pendenzen__btn pendenzen__btn--primary"
              onClick={() => {
                setNewPendenzProjectCode("");
                setShowNewPendenz(true);
              }}
            >
              + Neue Pendenz
            </button>
          )}
          {isAll && (
            <button
              type="button"
              className="pendenzen__btn pendenzen__btn--primary"
              onClick={() => {
                setNewListProjectCode("");
                setShowNewList(true);
              }}
            >
              + Neue Pendenzenliste
            </button>
          )}
        </div>
      </div>

      {error && (
        <div
          className="pendenzen__error"
          role="alert"
          onClick={() => setError(null)}
        >
          {error}
        </div>
      )}

      <div className="pendenzen__filters">
        <input
          type="search"
          className="pendenzen__search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={
            isAll
              ? "Projekt suchen (Nummer / Titel)..."
              : "Suche in Beschreibung / Verantwortlich / Projekt..."
          }
        />
        {!isAll && (
          <>
            <select
              value={filterArt}
              onChange={(e) => setFilterArt(e.target.value)}
            >
              <option value="">Alle Arten</option>
              {ART_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <select
              value={filterKategorie}
              onChange={(e) => setFilterKategorie(e.target.value)}
            >
              <option value="">Alle Kategorien</option>
              {KATEGORIE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <label className="pendenzen__filter-check">
              <input
                type="checkbox"
                checked={showErledigt}
                onChange={(e) => setShowErledigt(e.target.checked)}
              />
              <span>Erledigte anzeigen</span>
            </label>
          </>
        )}
      </div>

      {isAll ? (
        <div className="pendenzen__projects">
          {SPECIAL_LISTS.filter(
            (s) => !s.adminOnly || hasRole(user, ROLES.ADMIN)
          )
            .filter((s) =>
              search.trim()
                ? s.title.toLowerCase().includes(search.trim().toLowerCase())
                : true
            )
            .map((s) => {
              const counts = countsByProject[s.uuid] || { total: 0, open: 0 };
              return (
                <button
                  key={s.uuid}
                  type="button"
                  className="pendenzen__project-card pendenzen__project-card--special"
                  onClick={() =>
                    navigate(`/main-pendenzen/projekte/${s.code}`)
                  }
                  title="Allgemeine Pendenzenliste öffnen"
                >
                  <span className="pendenzen__project-main">
                    <span className="projekt-code">{s.title}</span>
                    <span className="projekt-title">
                      {s.adminOnly ? "Nur Geschäftsleitung" : "Für alle"}
                    </span>
                  </span>
                  <span className="pendenzen__project-counts">
                    <span className="count-open">{counts.open} offen</span>
                    <span className="count-total">{counts.total} total</span>
                  </span>
                </button>
              );
            })}
          {startedLists.length === 0 ? (
            <div className="pendenzen__empty pendenzen__empty--block">
              {search.trim()
                ? "Keine weitere Pendenzenliste gefunden."
                : 'Noch keine projektbezogenen Pendenzenlisten. Mit "Neue Pendenzenliste" eine für ein Projekt starten.'}
            </div>
          ) : (
            startedLists.map((proj) => {
              const counts = countsByProject[proj.id] || { total: 0, open: 0 };
              return (
                <button
                  key={proj.id}
                  type="button"
                  className="pendenzen__project-card"
                  onClick={() => goToProject(proj)}
                  title="Pendenzenliste öffnen"
                >
                  <span className="pendenzen__project-main">
                    <span className="projekt-code">{proj.code}</span>
                    <span className="projekt-title">{proj.title}</span>
                  </span>
                  <span className="pendenzen__project-counts">
                    <span className="count-open">{counts.open} offen</span>
                    <span className="count-total">{counts.total} total</span>
                  </span>
                </button>
              );
            })
          )}
        </div>
      ) : view === "board" ? (
        <PendenzenBoard
          columns={boardColumns}
          itemsByColumn={boardItems}
          onCardClick={(p) => setEditing(p)}
          onAddCard={(colKey) => goCreate(projectMap[colKey])}
          renderCard={(p) => <PendenzCard pendenz={p} projectLabel="" />}
        />
      ) : (
        renderTable(filtered, true)
      )}

      {/* Dialog: Neue Pendenzenliste (Projekt wählen) */}
      {showNewList && (
        <div className="pendenzen__modal-overlay" onClick={() => setShowNewList(false)}>
          <div
            className="pendenzen__modal pendenzen__modal--share"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pendenzen__modal-header">
              <h3>Neue Pendenzenliste</h3>
              <button
                type="button"
                className="pendenzen__modal-close"
                onClick={() => setShowNewList(false)}
                aria-label="Schliessen"
              >
                ×
              </button>
            </div>
            <div className="pendenzen__modal-body">
              <label className="pendenzen__field pendenzen__field--full">
                <span>Projekt *</span>
                <select
                  value={newListProjectCode}
                  onChange={(e) => setNewListProjectCode(e.target.value)}
                  autoFocus
                >
                  <option value="">-- Projekt wählen --</option>
                  {projectsWithoutList.map((p) => (
                    <option key={p.id} value={p.code}>
                      {p.code} – {p.title}
                    </option>
                  ))}
                </select>
              </label>
              {projectsWithoutList.length === 0 && (
                <p className="pendenzen__subtitle" style={{ marginTop: "8px" }}>
                  Für alle zugänglichen Projekte besteht bereits eine Liste.
                </p>
              )}
            </div>
            <div className="pendenzen__modal-footer">
              <div className="pendenzen__modal-footer-right">
                <button
                  type="button"
                  className="pendenzen__btn"
                  onClick={() => setShowNewList(false)}
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  className="pendenzen__btn pendenzen__btn--primary"
                  onClick={handleStartNewList}
                  disabled={!newListProjectCode}
                >
                  Liste öffnen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dialog: Neue Pendenz (zuerst Projekt wählen, dann dort erfassen) */}
      {showNewPendenz && (
        <div className="pendenzen__modal-overlay" onClick={() => setShowNewPendenz(false)}>
          <div
            className="pendenzen__modal pendenzen__modal--share"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pendenzen__modal-header">
              <h3>Neue Pendenz</h3>
              <button
                type="button"
                className="pendenzen__modal-close"
                onClick={() => setShowNewPendenz(false)}
                aria-label="Schliessen"
              >
                ×
              </button>
            </div>
            <div className="pendenzen__modal-body">
              <label className="pendenzen__field pendenzen__field--full">
                <span>Für welches Projekt? *</span>
                <select
                  value={newPendenzProjectCode}
                  onChange={(e) => setNewPendenzProjectCode(e.target.value)}
                  autoFocus
                >
                  <option value="">-- Projekt wählen --</option>
                  {accessibleProjects.map((p) => (
                    <option key={p.id} value={p.code}>
                      {p.code} – {p.title}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="pendenzen__modal-footer">
              <div className="pendenzen__modal-footer-right">
                <button
                  type="button"
                  className="pendenzen__btn"
                  onClick={() => setShowNewPendenz(false)}
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  className="pendenzen__btn pendenzen__btn--primary"
                  onClick={handleStartNewPendenz}
                  disabled={!newPendenzProjectCode}
                >
                  Weiter
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Direktes Bearbeiten in der persönlichen Ansicht (ohne Projektwechsel) */}
      {editing && (
        <PendenzEditModal
          pendenz={editing}
          projectUuid={editing.projectUuid}
          projectCode={projectMap[editing.projectUuid]?.code || ""}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setPendenzen((prev) =>
              prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x))
            );
            setEditing(null);
          }}
          onDeleted={(id) => {
            setPendenzen((prev) => prev.filter((x) => x.id !== id));
            setEditing(null);
          }}
        />
      )}
    </div>
  );
};

export default MyPendenzen;
